<template>
  <div class="message-item">
    <div class="message-item__header">
      <div class="message-item__avatar-wrap">
        <img
          v-if="message.userLogo"
          :src="message.userLogo"
          class="message-item__avatar"
          alt="avatar"
        />
        <div v-else class="message-item__avatar message-item__avatar--fallback">
          {{ userInitial }}
        </div>
      </div>
      <div class="message-item__meta">
        <span class="message-item__user" :title="displayName">{{
          displayName
        }}</span>
        <span class="message-item__time">{{ formattedCreateTime }}</span>
      </div>
    </div>

    <div
      class="message-item__body"
      :class="{
        'message-item__body--multiline': allowLineBreak,
        'message-item__body--no-reply': !allowReply,
      }"
    >
      {{ message.remark }}
    </div>

    <div class="message-item__actions">
      <button
        v-if="allowReply && canReplyCurrentLevel && !isThisReplyOpen"
        type="button"
        class="message-item__action-btn message-item__action-btn--reply"
        v-bind="remarkReplyTLabel ? { t_label: remarkReplyTLabel } : {}"
        @click="openReply"
      >
        {{ replyText }}
      </button>
      <button
        v-if="allowDelete && isOwnMessage && !message.isDelete"
        type="button"
        class="message-item__action-btn message-item__action-btn--delete"
        v-bind="remarkDeleteTLabel ? { t_label: remarkDeleteTLabel } : {}"
        @click="$emit('delete', message)"
      >
        {{ deleteText }}
      </button>
    </div>

    <MessageComposer
      v-if="isThisReplyOpen"
      class="message-item__reply-editor"
      :placeholder="replyPlaceholder"
      :submit-text="submitReplyText"
      :submit-t-label="remarkReplySubmitTLabel"
      :cancel-text="cancelReplyText"
      :show-cancel="true"
      :avatar="currentUserAvatar"
      :avatar-name="currentUserName"
      :max-length="maxLength"
      :allow-line-break="allowLineBreak"
      :enable-leave-guard="enableLeaveGuard"
      :submitting="submitting && isThisReplyOpen"
      @submit="submitReply"
      @cancel="cancelReply"
      @dirty-change="onReplyDraftDirty"
    />

    <div
      v-if="showThreadChildren && message.children && message.children.length"
      class="message-item__children"
    >
      <MessageItem
        v-for="child in message.children"
        :key="child.id"
        :message="child"
        :show-thread-children="showThreadChildren"
        :active-reply-message-id="activeReplyMessageId"
        :allow-reply="allowReply"
        :allow-delete="allowDelete"
        :max-depth="maxDepth"
        :reply-text="replyText"
        :cancel-reply-text="cancelReplyText"
        :delete-text="deleteText"
        :submit-reply-text="submitReplyText"
        :reply-placeholder="replyPlaceholder"
        :current-user-avatar="currentUserAvatar"
        :current-user-name="currentUserName"
        :remark-reply-t-label="remarkReplyTLabel"
        :remark-delete-t-label="remarkDeleteTLabel"
        :remark-reply-submit-t-label="remarkReplySubmitTLabel"
        :allow-line-break="allowLineBreak"
        :enable-leave-guard="enableLeaveGuard"
        :submitting="submitting"
        @reply="$emit('reply', $event)"
        @delete="$emit('delete', $event)"
        @replying-change="$emit('replying-change', $event)"
        @remark-draft-dirty="$emit('remark-draft-dirty', $event)"
      />
    </div>
  </div>
</template>

<script>
import MessageComposer from "./MessageComposer.vue";
import {
  formatMessageDateTime,
  resolveDisplayInitial,
  resolveLocalizedPersonName,
} from "@/_services/messageBoard.service";

export default {
  name: "MessageItem",
  components: {
    MessageComposer,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    allowReply: {
      type: Boolean,
      default: true,
    },
    allowDelete: {
      type: Boolean,
      default: false,
    },
    maxDepth: {
      type: Number,
      default: 3,
    },
    replyText: {
      type: String,
      default: "Reply",
    },
    cancelReplyText: {
      type: String,
      default: "Cancel",
    },
    deleteText: {
      type: String,
      default: "Delete",
    },
    submitReplyText: {
      type: String,
      default: "Send",
    },
    replyPlaceholder: {
      type: String,
      default: "Reply to this message...",
    },
    currentUserAvatar: {
      type: String,
      default: "",
    },
    currentUserName: {
      type: String,
      default: "",
    },
    activeReplyMessageId: {
      type: [Number, String],
      default: null,
    },
    showThreadChildren: {
      type: Boolean,
      default: true,
    },
    remarkReplyTLabel: {
      type: String,
      default: "",
    },
    remarkDeleteTLabel: {
      type: String,
      default: "",
    },
    remarkReplySubmitTLabel: {
      type: String,
      default: "",
    },
    maxLength: {
      type: Number,
      default: 1500,
    },
    enableLeaveGuard: {
      type: Boolean,
      default: false,
    },
    allowLineBreak: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    langId() {
      return localStorage.getItem("currentLangId") || "936";
    },
    isThisReplyOpen() {
      if (this.activeReplyMessageId == null || this.activeReplyMessageId === "")
        return false;
      return String(this.activeReplyMessageId) === String(this.message.id);
    },
    canReplyCurrentLevel() {
      const level = Number(this.message.level || 0);
      return level < this.maxDepth;
    },
    displayName() {
      const name = resolveLocalizedPersonName(this.message, this.langId);
      return name || "-";
    },
    formattedCreateTime() {
      return formatMessageDateTime(this.message?.createTime);
    },
    userInitial() {
      return resolveDisplayInitial(this.displayName);
    },
    currentAccountUserId() {
      const u = this.$store?.state?.authentication?.user || {};
      return Number(
        u?.accountUserId ||
          u?.AccountUserId ||
          u?.accountUserID ||
          u?.AccountUserID ||
          0,
      );
    },
    isOwnMessage() {
      return (
        this.currentAccountUserId > 0 &&
        Number(this.message?.accountUserId || 0) === this.currentAccountUserId
      );
    },
  },
  methods: {
    openReply() {
      this.$emit("replying-change", { id: this.message.id, isReplying: true });
    },
    cancelReply() {
      this.onReplyDraftDirty(false);
      this.$emit("replying-change", { id: this.message.id, isReplying: false });
    },
    onReplyDraftDirty(dirty) {
      this.$emit("remark-draft-dirty", dirty);
    },
    submitReply(content) {
      this.$emit("reply", {
        parent: this.message,
        content,
      });
      this.$emit("replying-change", { id: this.message.id, isReplying: false });
    },
  },
};
</script>

<style scoped>
.message-item {
  padding: 0;
  border: none;
  background: #fff;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.message-item__header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 4px;
}

.message-item__avatar-wrap {
  flex: 0 0 auto;
}

.message-item__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.message-item__avatar--fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f2f4f7;
  color: #667085;
  font-size: 12px;
  font-weight: 600;
}

.message-item__meta {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex: 1;
  min-width: 0;
  flex-wrap: nowrap;
}

.message-item__user {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  color: #1d2939;
}

.message-item__time {
  flex-shrink: 0;
  font-size: 14px;
  color: #98a2b3;
}

.message-item__body {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
  box-sizing: border-box;
  font-size: 14px;
  color: #344054;
  line-height: 1.4;
  margin-left: 38px;
}

.message-item__body--multiline {
  white-space: pre-wrap;
}

.message-item__body--no-reply {
  margin-left: 42px;
  margin-top: -12px;
}

.message-item__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 4px;
  margin-left: 38px;
}

.message-item__action-btn {
  border: none;
  background: transparent;
  padding: 0;
  border-radius: 0;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.2;
  cursor: pointer;
  transition: color 0.15s ease;
}

.message-item__action-btn--reply {
  color: #667eea;
}

.message-item__action-btn--reply:hover {
  color: #4f46e5;
  text-decoration: none;
}

.message-item__action-btn--delete {
  color: #98a2b3;
}

.message-item__action-btn--delete:hover {
  color: #d92d20;
  text-decoration: none;
}

.message-item__children {
  margin-top: 8px;
  margin-left: 38px;
  padding-left: 20px;
  border-left: 1px solid #ececf2;
  display: grid;
  gap: 10px;
  min-width: 0;
  max-width: 100%;
}

.message-item__reply-editor {
  margin-top: 8px;
  margin-left: 38px;
}
</style>
